/**
 * @module profile/components/portfolio/entry_details
 */
import React, { PropTypes } from 'react';
import { v4 } from 'node-uuid';
import SingleEntry from 'common/components/modal/single_entry';
import EntryDetailsComponent from 'profile/components/portfolio/entry_details_component';
import EntryDetailsHeader from 'profile/components/portfolio/entry_details_header';

let screenfull = null;
if (__CLIENT__) {
  screenfull = require('screenfull');
} else {
  screenfull = { toggle: () => {} };
}

/**
 * Component for the portfolio single entry extending the base class.
 * @author Kuba Siemiątkowski <kuba@crowdspring.com>
 */
class EntryDetails extends SingleEntry {
  constructor(props) {
    super(props);

    this.state = {
      leftDrawerVisible: false,
      entry: props.entry,
    };

    this.nextPage = this.nextPage.bind(this);
    this.prevPage = this.prevPage.bind(this);
    this.toggleFullscreen = this.toggleFullscreen.bind(this);
    this.toggleLeftDrawer = this.toggleLeftDrawer.bind(this);
  }

  componentDidMount() {
    this.getEntryPage();
  }

  componentWillUnmount() {
    this.props.setEntryDetailsPage(0);
  }

  getEntryPage() {
    const { pageState, visibleEntries } = this.props;
    const entryPage = (pageState.currentPage - 1) * pageState.pageSize;
    const { entry } = this.state;
    let pageNumber = entryPage || 1;

    /* the entry may be missing, e.g. due to a network error */
    if (entry) {
      const entryIdx = visibleEntries.findIndex(e => e.id === entry.id);
      pageNumber = entryPage + entryIdx + 1;
    }

    this.props.setEntryDetailsPage(pageNumber);
  }

  toggleFullscreen() {
    screenfull.toggle();
  }

  toggleLeftDrawer() {
    this.setState({
      leftDrawerVisible: false,
    });
  }

  /*
   * Perform the request fetching new data when next page button
   * is selected.
   */
  onNextPage(pageNum) {
    const { profileUsername, visibleEntries, pageState } = this.props;

    /* the entry may be missing, e.g. due to a network error */
    if (!this.state.entry) {
      return;
    }

    const visibleEntryPos = visibleEntries.findIndex(e => e.id === this.state.entry.id);
    let entry;

    if (visibleEntryPos < visibleEntries.length - 1) {
      return this.props.setEntryDetailsPage(pageNum)
      .then(() => {
        const newId = visibleEntries[visibleEntryPos + 1].id;
        entry = { ...pageState.entriesById[newId] };

        this.setState({ entry });
      });
    }

    return this.props.setEntryDetailsPage(pageNum)
    .then(() => {
      this.props.fetchEntry(profileUsername, pageNum - 1)
      .then((entryId) => {
        if (entryId != null) {
          entry = { ...pageState.entriesById[entryId] };

          this.setState({ entry });
        }
      });
    });
  }

  /*
   * Perform the request fetching new data when prev page button
   * is selected.
   */
  onPrevPage(pageNum) {
    const { profileUsername, pageState, visibleEntries } = this.props;
    const visibleEntryPos = visibleEntries.findIndex(e => e.id === this.state.entry.id);
    let entry;

    if (visibleEntryPos > 0) {
      return this.props.setEntryDetailsPage(pageNum)
      .then(() => {
        const newId = visibleEntries[visibleEntryPos - 1].id;
        entry = { ...pageState.entriesById[newId] };

        this.setState({ entry });
      });
    }

    return this.props.setEntryDetailsPage(pageNum)
    .then(() => {
      this.props.fetchEntry(profileUsername, pageNum - 1)
      .then((entryId) => {
        if (entryId != null) {
          entry = { ...pageState.entriesById[entryId] };

          this.setState({ entry });
        }
      });
    });
  }

  render() {
    const { toggleEntryDetails, itemsCount, entryDetailsState, userAccess, creative } = this.props;
    const { leftDrawerVisible, entry } = this.state;
    const detailsState = {
      leftDrawerVisible,
      zoom: entryDetailsState.zoom,
      maxScale: entryDetailsState.maxScale,
    };

    return super.render([
      <EntryDetailsHeader
        creative={creative}
        userAccess={userAccess}
        entry={entry}
        pageNumber={entryDetailsState.currentPage}
        itemsCount={itemsCount}
        onPrevPage={this.prevPage}
        onNextPage={this.nextPage}
        onToggleEntryDetails={toggleEntryDetails}
        onToggleLeftDrawer={this.toggleLeftDrawer}
        onDeleteEntry={this.props.deleteEntry}
        key={v4()}
      />,
      <EntryDetailsComponent
        detailsState={detailsState}
        entry={entry}
        onZoomIn={this.props.zoomInEntry}
        onZoomOut={this.props.zoomOutEntry}
        onToggleFullscreen={this.toggleFullscreen}
        onToggleLeftDrawer={this.toggleLeftDrawer}
        key={v4()}
      />
    ]);
  }
}

/**
 * PropTypes.
 *
 * @property {object} creative - author data object
 * @property {string} profileUsername - creative's username
 * @property {object} userAccess - viewing user's privileges and info
 * @property {object} entryDetailsState - object with single entry modal state
 * @property {object} pageState - object with portfolio page state
 * @property {object} user - user data object
 * @property {array} visibleEntries - list of all visible entries for current gallery page
 * @property {object} entry - entry data object
 * @property {number} currentPage - single entry page number
 * @property {func} zoomInEntry - action creator to zoom-in the image
 * @property {func} zoomOutEntry - action creator to zoom-out the image
 * @property {func} resetZoom - action creator to reset zoom to 1
 * @property {func} fetchEntry - action creator for fetching a single entry by id
 * @property {func} setEntryDetailsPage - action creator for saving the current entry details
                    page number in the state
 * @property {func} toggleEntryDetails - show/hide single entry modal
 */
EntryDetails.propTypes = {
  creative: PropTypes.object,
  profileUsername: PropTypes.string,
  userAccess: PropTypes.object,
  entryDetailsState: PropTypes.object,
  pageState: PropTypes.object,
  user: PropTypes.object,
  visibleEntries: PropTypes.array,
  entry: PropTypes.object,
  itemsCount: PropTypes.number,
  currentPage: PropTypes.number,
  zoomInEntry: PropTypes.func,
  zoomOutEntry: PropTypes.func,
  resetZoom: PropTypes.func,
  fetchEntry: PropTypes.func,
  setEntryDetailsPage: PropTypes.func,
  toggleEntryDetails: PropTypes.func,
};

export default EntryDetails;



// WEBPACK FOOTER //
// ./profile/components/portfolio/entry_details.jsx